import React, { memo, useEffect, useState } from 'react';
import type { EditorBlock } from '@/store/editorSlice';
import {
  withBlockBackground,
  buttonSurfaceStyle,
} from './blockBackgroundStyle';
// WEB-2054: редкие виды блоков едут ЛЕНИВЫМИ кусками — статический импорт
// общего рендерера приводил в первый экран покупателя все 60 редких веток
// разом (196 КБ несжатого текста), включая портал подписок и графики, которых
// в его воронке нет. Дверь спрашивает у карты видов, какой кусок нужен ЭТОМУ
// блоку, и берёт только его; куски греются заранее по составу воронки из
// ответа резолва. Держится сторожем сборки: `FIRST_SCREEN_DEFERRED_MODULES` в
// build-guards/publicEntryGuard.ts краснеет, если импорт вернут статическим.
import { PublicBlockRendererGate } from './PublicBlockRendererGate';
// WEB-2054 (починка ревизии, Р1 и Р4): денежные и частые виды едут С ПЕРВЫМ
// ЭКРАНОМ. Импорт СТАТИЧЕСКИЙ намеренно — в этом весь смысл переезда: селектор
// тарифа обязан смонтироваться тем же кадром, что и кнопка оплаты, иначе
// умолчание тарифа не успевает попасть в значения формы и чекаут уходит по
// цене кнопки (см. шапку PublicBlockRendererCommon.tsx).
import { PublicBlockRendererCommon } from './PublicBlockRendererCommon';
import { CORE_COMMON_BLOCK_TYPES } from '@/player/publicBlockChunkMap';
import { withBlockFont } from './blockFont';
import { withBlockSpacing } from './blockSpacing';
import { withBlockAlign } from './blockAlign';
import { resolveBlockBorder } from './blockBorder';
import {
  withBlockInnerSurface,
  innerSurfaceFieldsOf,
  resolveContentTextColor,
} from './blockInnerSurface';
import { buttonLabelField } from './buttonLabelField';
import { captionChipAppearance } from './captionChipAppearance';
import { imageFrameStyle, imageWrapperStyle } from './imageFrameStyle';
// WEB-1994: место под картинку до её загрузки (почему `aspect-ratio: auto` —
// в докблоке помощника).
import { reserveImageSpace } from './imageSpaceReserve';
import { useBlockFormValue } from './FormValuesContext';
import { type PublicStripePrice } from '@/utils/formatStripePrice';
import { interpolate, interpolateHtml } from '@/player/interpolate';
import type { VariableContext } from '@/player/variable-context';
import { RichTextContent } from './RichTextContent';
import {
  asRichField,
  hasRichText,
  richFieldToPlainText,
} from '@/types/rich-text';
import { safeHref } from '@/utils/isAllowedUrl';
// WEB-2262: метки исходящего адреса у кнопки-ссылки — подстановка живёт в
// отдельном файле, зеркало серверной функции под двусторонним сторожем.
import {
  expandOutboundUrlMacros,
  hasOutboundMacros,
  type OutboundMacroValues,
} from '@/utils/outboundUrlMacros';
import { linkifyLegalMentions } from '@/utils/stripTermsDuplicate';
import { primaryButtonStyle } from '@/themes';
import type { ButtonStyle } from '@/themes/theme.types';
import { useEditorPreview } from './EditorPreviewContext';
import { useStoreLinks } from './StoreLinksContext';
import { useExternalExit } from './externalExitContext';
import { AuthorHintBox } from './AuthorHintBox';
// WEB-2158: явное согласие — свой компонент, потому что у блока ДВА значения
// формы, а второй хук подписки нельзя звать внутри switch (см. его докблок).
import { SensitiveConsentBlock } from './SensitiveConsentBlock';
import { isStoreAutoUrl, resolveStoreAutoUrl } from '@/utils/storeAutoUrl';
// WEB-916 п.2: ссылки условий красились акцентом (3.68-3.93:1) — токен ссылки
// держит текстовый порог 4.5:1.
import { LINK_ON_SURFACE_COLOR } from './linkColor';
import {
  getPublicQuizStrings,
  type PublicQuizStrings,
} from '@/utils/publicQuizStrings';
import { getStylePackById } from '@/themes/stylePacks';
import { resolveSelectedOptionColor } from './optionSelectedColor';
// WEB-1651-3: цвет самой отметки нативных чекбоксов/радио (accent-color).
import { resolveOptionAccent } from './optionAccentColor';
// WEB-3447, заход 2: типографика пака — через дверь ленивого куска.
import { subTitleTypeStyle, titleLineHeight } from '@/themes/styleKitDoor';
// WEB-1672: цвет подписей вариантов — отдельный слой поверх того, что решает
// цвет сегодня (см. порядок силы слоёв в optionRowStyle.ts).
import {
  optionTextColorOverride,
  optionFontSizeOverride,
  optionRowShadow,
  optionGapOverride,
  optionContentGapOverride,
  optionRadiusOverride,
  optionIndicatorHidden,
  optionIndicatorAlignOverride,
  optionRowIndicatorOverride,
  OPTION_INDICATOR_HIDDEN_STYLE,
  optionBackgroundOverride,
  optionPaddingOverride,
  optionBorderOverride,
  optionFontWeightOverride,
  optionMinHeightOf,
} from './optionRowStyle';
import {
  optionCellOverride,
  optionDescriptionOf,
  optionListGridOverride,
} from './optionGrid';
import { OptionRowContent } from './OptionRowContent';
// Задание stylepack/option-indicator: место и свой вид отметки выбора.
import { OptionIndicatorBox } from './OptionIndicatorBox';
import {
  optionIndicatorInputOverride,
  optionIndicatorRowOverride,
} from './optionIndicator';
import { OptionScaleLabels } from './scaleLabels';
// Задание stylepack/text: межстрочный, межбуквенный, жирность, регистр.
import { buttonLookOverride, textLookOverride } from './textLook';
import { SkipLinkButton, SkipOnlyLink } from './SkipLinkButton';
import { skipOnlyActive } from './skipLink';
// Задание stylepack/text-icon: значок перед строкой текста в плашке.
import { TEXT_ICON_BLOCK_TYPES, textIconParts } from './textIcon';
// WEB-1694: оформление текста ВНУТРИ поля ввода отдельно от подписи над ним
// (порядок силы слоёв и решение про подсказку — в inputFieldStyle.ts).
import {
  inputLabelStyleOverride,
  inputTextColorOverride,
  inputTextFontSizeOverride,
  // Слово владельца 21.10.2026: вид самого поля (фон, рамка, скругление,
  // высота, отступ, тень, подсказка, рамка в фокусе).
  inputBoxStyleOverride,
  inputLookClassName,
} from './inputFieldStyle';
import { pickStrings } from '@/i18n';
// WEB-2057: подсказки АВТОРУ — из своего маленького словаря. Полный словарь
// конструктора этому файлу нельзя: его рисует ПУБЛИЧНАЯ страница покупателя.
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
// WEB-2921: доли ядра — перенесённые слово в слово ветки и компоненты
// (core-blocks/<Вид>Renderer.tsx). Едут в первом экране вместе с ядром:
// импорт статический, ленивой двери у них нет.
import { RedirectBlock } from './core-blocks/RedirectRenderer';
import { WalletsExpressButtons } from './core-blocks/WalletsExpressRenderer';
import { renderTimer } from './core-blocks/TimerRenderer';
import { renderPaymentBanners } from './core-blocks/PaymentBannersRenderer';
import { renderPriceBlock } from './core-blocks/PriceBlockRenderer';
import { NextBackPair } from './core-blocks/NextBackPairRenderer';

/** Рамка плашки варианта по решению стиль-пака — основа своей рамки автора. */
const OPTION_PACK_BORDER = {
  width: 'var(--option-border-width, 2px)',
  color: 'var(--option-border-color, var(--color-border-primary, #f0f0f0))',
  selectedColor: 'var(--option-selected-border, var(--color-primary, #1677ff))',
};

/** Отступ плашки по решению пака — опора компенсации рамки выбранной. */
const OPTION_PACK_PAD = {
  y: 'var(--option-pad-y, 10px)',
  x: 'var(--option-pad-x, 14px)',
};
// Задание stylepack/title-side-image: картинка слева от текста заголовка.
import { renderTitleSideImage } from './core-blocks/TitleSideImageRenderer';

/**
 * WEB-1993 п.5: наблюдатель рендеров блока. Ставится ТОЛЬКО тестом-сторожем
 * («клик по варианту не перерисовывает соседние блоки экрана») — иначе
 * считать рендеры настоящего компонента нечем, а подделка вместо него
 * доказывала бы свойства подделки. В бою — одна проверка на null.
 */
let blockRenderObserver: ((blockId: string) => void) | null = null;

/**
 * WEB-2262: адрес кнопки-ссылки с подставленными метками или `null`, если
 * подставлять нечего (плеер значений не дал, меток в адресе нет, подстановка
 * ничего не изменила) — тогда уход остаётся родным переходом по ссылке (класс
 * WEB-1119). Подстановка идёт ПОСЛЕ проверки годности адреса и только в
 * строку параметров — тот же порядок, что на сервере (resolveExitTarget).
 * Результат ещё раз проходит дверь очистки у вызывающего (WEB-1911).
 */
function linkMacroHref(
  cleaned: string,
  values: OutboundMacroValues | undefined,
): string | null {
  if (values === undefined) return null;
  if (!hasOutboundMacros(cleaned)) return null;
  const expanded = expandOutboundUrlMacros(cleaned, values);
  return expanded === cleaned ? null : expanded;
}
export function __setBlockRenderObserverForTests(
  observer: ((blockId: string) => void) | null,
): void {
  blockRenderObserver = observer;
}

interface PublicBlockRendererProps {
  block: EditorBlock;
  /** When set, overrides the CSS variable fallback color for content text. */
  textColor?: string;
  /** Resolved Stripe prices, so price blocks can show real amounts. */
  stripePrices?: PublicStripePrice[];
  /** WEB-832 п.4: фактическая позиция экрана — для блока «Шаг X из Y». */
  stepPosition?: { current: number; total: number };
  /**
   * F6: Variable substitution context for {{interpolation}}.
   * When provided, text fields are processed through interpolate().
   * URL fields (href, url) are NEVER interpolated (D7 RATIFIED).
   * Omit to skip interpolation (dashboard editor preview, etc.).
   */
  varCtx?: VariableContext;
  /**
   * WEB-447 wave1 slice2: the resolved theme's buttonStyle
   * ('solid'|'outlined'|'pill'|'gradient'), read by primaryButtonStyle() for
   * every primary CTA (button_next, button_to_pay). Undefined (no theme
   * resolved) defaults to a high-contrast pill.
   */
  buttonStyle?: ButtonStyle;
  /** WEB-924: id стиль-пака — раскладка фото-опций в Extended (imagery.*). */
  stylePackId?: string;
  /**
   * WEB-936 п.9: блоки текущего экрана — тот же вход, что уже получают
   * BuyNowButton/PaywallStickyHeader для resolvePriceId. Прокидывается только
   * в PublicBlockRendererExtended, так `payment_terms` находит соседний
   * `plan_selector` и читает его живой выбор тарифа. Необязательный — не
   * передан → прежнее поведение (Extended сам фолбэчится).
   */
  screenBlocks?: EditorBlock[];
  /**
   * WEB-1302: все блоки воронки (не только текущего экрана) — тот же вход,
   * что resolvePriceId уже получает через BuyNowButton, для кросс-экранного
   * «Выбор тарифа на одном экране → оплата на другом» (WEB-703 п.3).
   * Экспресс-кошельки (stripe_wallets_express) читают его наравне с обычной
   * кнопкой оплаты — иначе выбор тарифа с предыдущего экрана они бы теряли.
   */
  funnelBlocks?: EditorBlock[];
  /**
   * WEB-1113 доводка (проверка пачек 23.08, FC3a): id блоков текущего экрана,
   * скрытых правилом видимости — тот же вход, что уже получают BuyNowButton/
   * PaywallStickyHeader для resolvePriceId. Раньше сюда не доезжал вовсе, так
   * что экспресс-кошелёк (stripe_wallets_express, WalletsExpressButtons ниже)
   * мог списать цену/выбор тарифа скрытого блока через cross-screen ветку
   * resolvePriceId (funnelBlocks не фильтруется). Необязательный — не передан
   * → прежнее поведение (второй заслон внутри resolvePriceId просто не
   * применяется, как и раньше).
   */
  hiddenBlockIds?: Set<string>;
  /**
   * WEB-1696 — ЖИВОЕ ПРОДВИЖЕНИЕ ЭКРАНА для блока «Кнопка Далее».
   *
   * Раньше визард квиза (`QuizScreenWizard`) перехватывал тип `button_next`
   * и рисовал СВОЮ кнопку с обработчиком, мимо этого рендера. Из-за этого до
   * кнопки не доезжала НИ ОДНА настройка блока: ни свой кегль, ни «Фон
   * кнопки», ни «Цвет текста внутри элемента», ни общие обёртки подложки /
   * внутренних отступов / расстояния / шрифта — они все живут здесь, на
   * выходе общего рендера. Классическая «одна дверь из двух»: панель писала
   * поля в блок, а экран читал его другим кодом.
   *
   * Дверь теперь одна: рисует кнопку общий рендер, а визард передаёт сюда
   * ровно ту часть, которой у рендера быть не может — обработчик перехода,
   * свой data-testid и признак «на экране есть незаполненные обязательные»
   * (класс приглушения из стиль-пака, WEB-922). Не передан — кнопка
   * декоративная, как и была (превью палитры, пейволл-ветки).
   */
  onAdvance?: () => void;
  /**
   * Задание stylepack/skip-link: переход «без ответа» по ссылке «Пропустить»
   * под кнопкой «Далее» (SkipLinkButton.tsx). Не передан — ссылка ведёт как
   * сама кнопка (`onAdvance`).
   */
  onSkip?: () => void;
  /**
   * Задание stylepack/next-back-pair: обработчик «назад» визарда — тот же,
   * что у кнопки «назад» шапки; на первом экране и на холсте его нет. Нужен
   * только кнопке «далее» с `backBeside` (ряд — core-blocks/NextBackPairRenderer.tsx).
   */
  onBack?: () => void;
  /** WEB-1696: data-testid кнопки продвижения (визарды зовут её по-своему). */
  advanceTestId?: string;
  /** WEB-1696: WEB-922 приглушение — незаполненные обязательные на экране. */
  advanceDimmed?: boolean;
  /** WEB-1696: подпись-фолбэк кнопки (на последнем экране — «Завершить»). */
  advanceFallbackLabel?: string;
  /**
   * Пачка настроек 2 (повтор Slimkit): вид кнопки продвижения из стиль-пака
   * (высота, текст, тень, затемнение неактивной — ctaPackButtonStyle) и стиль
   * её обёртки (в прибитой снизу панели — без нижнего поля). Идёт после стиля
   * темы и до полей блока. Не передано — вид прежний.
   */
  advanceButtonStyle?: React.CSSProperties;
  advanceWrapStyle?: React.CSSProperties;
  /**
   * WEB-1709 (живая проверка 01.09): блок идёт сразу за блоком, которому автор
   * задал расстояние снизу. Тогда он получает обёртку расстояния и без своей
   * настройки — иначе неявное верхнее поле его корня (у `<p>`/`<h1>` оно из
   * таблицы браузера) схлопывалось бы с заданным и держало пол в 16px. Разбор
   * и замеры — в `blockSpacing.tsx`, `blocksFollowingExplicitSpacing`. Признак
   * считает УРОВЕНЬ ЭКРАНА: только он знает, кто чей сосед в разметке.
   */
  followsExplicitSpacing?: boolean;
}

/**
 * WEB-2921: контекст функций отрисовки долей ядра (`core-blocks/`). Ветка,
 * чьё тело уехало в долю, получает замыкания ядра объектом, а не десятком
 * аргументов: хуки остаются в ядре (их нельзя звать внутри `switch`), доля —
 * чистая функция отрисовки. Поле добавляется, когда очередной ветке оно
 * понадобилось, — лишнего доля не видит.
 *
 * ЧТО ОСТАЛОСЬ В ЯДРЕ НАМЕРЕННО (решение оркестратора 09.10, заход 2). Ветки
 * `button_to_pay` и `stripe_checkout_form` в долю не вынесены: ядро едет в
 * первом экране покупателя, и каждая функция-доля стоит байтов на проводе
 * (контекст объектом + сигнатура). Замер сборкой: все четыре денежные доли
 * дали JS первого экрана 164486 / провод 168480 при потолках 164485 / 168461
 * (с допуском 64) — не влезает; без этих двух — 164426 / 168420. Ядро и так
 * ниже порога больших файлов (1636 строк). Цену самого рефакторинга (+32
 * байта: этот контекст объектом и функции долей) потолки первого экрана
 * получили отдельной строкой истории (WEB-2921, publicEntryGuard.ts и
 * publicEntryWireWeightGuard.ts) — ровно на замер, без запаса.
 */
export interface CoreRenderContext {
  block: EditorBlock;
  stylePackId?: string;
  /** Словарь служебных строк на языке воронки (`varCtx.system.locale`). */
  publicStrings: PublicQuizStrings;
  /** Контекст подстановок и язык воронки. */
  varCtx?: VariableContext;
  /** Цены Stripe проекта (сумма по цене блока). */
  stripePrices?: PublicStripePrice[];
}

/**
 * Пак с текстом варианта по центру (`type.optionAlign: 'center'`, outline-pills,
 * WEB-3718). Одна дверь для строки и подписи у значка: у остальных паков и без
 * пака выравнивание не задаётся — разметка строки байт в байт прежняя.
 */
function packOptionCentered(stylePackId: string | undefined): boolean {
  return (
    !!stylePackId && getStylePackById(stylePackId)?.type.optionAlign === 'center'
  );
}

function optionAlignOverride(
  stylePackId: string | undefined,
): React.CSSProperties {
  return packOptionCentered(stylePackId)
    ? {
        justifyContent: 'var(--option-justify, normal)',
        textAlign: 'var(--option-text-align)' as React.CSSProperties['textAlign'],
      }
    : {};
}

function PublicBlockRendererInner({
  block,
  textColor,
  stripePrices,
  varCtx,
  buttonStyle,
  stepPosition,
  stylePackId,
  screenBlocks,
  funnelBlocks,
  hiddenBlockIds,
  onAdvance,
  onSkip,
  onBack,
  advanceTestId,
  advanceDimmed,
  advanceFallbackLabel,
  advanceButtonStyle,
  advanceWrapStyle,
  followsExplicitSpacing,
}: PublicBlockRendererProps) {
  // WEB-1993 п.3: подписка на ОДНО значение — своего блока. Раньше здесь
  // читался весь словарь ответов через общий контекст, и любой ответ
  // покупателя будил каждый блок экрана (контекст перерисовывает всех
  // подписчиков, `React.memo` тут бессилен). Все обращения ниже и так шли
  // ровно по `block.id` — поведение то же, разбудить может только своё
  // значение.
  const { value: blockValue, setValue } = useBlockFormValue(block.id);
  // Единый признак «вариант выбран» для одиночного и множественного
  // выбора: у первого значение одно, у второго — список через запятую.
  const isOptionSelected = (blockId: string, optionId: string): boolean => {
    const raw = blockId === block.id ? blockValue : '';
    return raw === optionId || raw.split(',').includes(optionId);
  };
  // Наблюдатель рендеров — только для сторожа WEB-1993 п.5; в бою это одна
  // проверка на null за отрисовку.
  if (blockRenderObserver) blockRenderObserver(block.id);
  // WEB-846: в превью редактора клик по кнопке-ссылке не должен исполнять
  // боевой переход — только всплывать до делегированного выделения блока.
  const { isEditorPreview } = useEditorPreview();
  // WEB-1906: адреса магазинов проекта — их подставляет маркер `w2a://store`
  // в поле адреса кнопки-ссылки (см. ветку 'button_link' ниже). Хук зовётся
  // тут, безусловно: внутри switch он нарушил бы порядок хуков.
  const storeLinks = useStoreLinks();
  // WEB-2124: та же единая дверь ухода — для кнопки-ссылки (ветка
  // 'button_link' ниже). Хук зовётся тут, безусловно: внутри switch он
  // нарушил бы порядок хуков (шрам WEB-1993).
  const externalExit = useExternalExit();
  // WEB-1702 п.2: цвет текста ЭТОГО блока сильнее общего цвета воронки —
  // лестница слоёв описана в blockInnerSurface.tsx (resolveContentTextColor).
  // Раньше общий цвет запекался инлайном и молча убивал блочную настройку,
  // как только его задавал ИИ-чат.
  const contentTextColor = resolveContentTextColor(block, textColor);
  const primaryColor = contentTextColor ?? 'var(--color-text-primary, #1a1a1a)';
  // WEB-785 (возврат QA 2026-08-01): служебные фолбэки блоков (кнопка покупки,
  // подписи блока согласия, спиннер) были зашиты по-английски и торчали поверх
  // русского контента. Язык воронки лежит в varCtx.system.locale (его кладут
  // оба визарда); без контекста словарь отдаёт прежние английские строки.
  const publicStrings = getPublicQuizStrings(varCtx?.system?.locale);

  // WEB-674: track broken image loads so a bad/non-image URL shows a helpful
  // placeholder instead of failing silently. Reset whenever imageUrl changes
  // (e.g. author fixes the link) so a stale error does not linger.
  const [imageLoadError, setImageLoadError] = useState(false);
  useEffect(() => {
    setImageLoadError(false);
  }, [block.imageUrl]);

  /**
   * F6 helpers — apply variable interpolation when varCtx is present.
   * interp()     → for plain text fields (React text nodes, auto-escaped).
   * interpHtml() → for dangerouslySetInnerHTML (values HTML-escaped, XSS-safe).
   * URL fields (href, src, url) must NOT pass through here (D7 RATIFIED).
   */
  const interp = (text: string | undefined): string =>
    text == null ? '' : varCtx ? interpolate(text, varCtx) : text;

  const interpHtml = (html: string): string =>
    varCtx ? interpolateHtml(html, varCtx) : html;

  /**
   * WEB-672 2a: rich-field render path for the ratified fields
   * (RICH_FIELDS_BY_BLOCK_TYPE mirror). Plain strings keep the exact legacy
   * output (bare text node + interpolation); spans render as styled <span>/<a>.
   */
  const richText = (value: string | undefined, fallback?: React.ReactNode) => (
    <RichTextContent
      value={asRichField(value)}
      varCtx={varCtx}
      fallback={fallback}
    />
  );

  /**
   * Задание stylepack/text-icon (слово владельца 21.10.2026): текст блока со
   * значком перед строкой — ряд ВНУТРИ текстового тега (стиль тега, его
   * выравнивание и вид текста не трогаются). Нет годного адреса — ровно
   * прежний `richText`, разметка байт в байт.
   */
  const textWithIcon = (value: string | undefined) => {
    const text = richText(value);
    const parts = TEXT_ICON_BLOCK_TYPES.has(block.type)
      ? textIconParts(block)
      : null;
    if (!parts) return text;
    const img = (
      <img src={parts.src} alt="" aria-hidden="true" style={parts.img} />
    );
    return (
      <span data-testid="text-icon-row" style={parts.row}>
        <span aria-hidden="true" style={parts.holder}>
          {parts.plate ? (
            <span data-testid="text-icon-plate" style={parts.plate}>
              {img}
            </span>
          ) : (
            img
          )}
        </span>
        <span style={parts.text}>{text}</span>
      </span>
    );
  };

  /** Truthiness gate replacement for rich fields (parity for plain strings). */
  const hasText = (value: string | undefined): boolean =>
    hasRichText(asRichField(value));

  /**
   * WEB-809 wave 2b: подпись варианта — displayLabel (rich) при наличии,
   * иначе plain label байт-в-байт. label остаётся значением ответа.
   * WEB-2973: подпись идёт через подстановку (покупатель видел сырое
   * `{{answer.…}}`); подставляется только ПОКАЗ — значение ответа (label в
   * значении поля и в правилах логики) прежнее.
   */
  const optionDisplay = (opt: {
    label: string;
    displayLabel?: string;
  }): React.ReactNode =>
    hasRichText(asRichField(opt.displayLabel))
      ? richText(opt.displayLabel)
      : interp(opt.label);

  /**
   * Задание stylepack/option-description: описание варианта — вторая строка
   * под подписью (только single_choice и multiple_choice). Подстановка — та
   * же, что у подписи: только показ, значение ответа — по-прежнему label.
   */
  const optionDescriptionNode = (opt: {
    description?: string;
  }): React.ReactNode => {
    const text = optionDescriptionOf(opt);
    return text === undefined ? undefined : interp(text);
  };

  // WEB-672 atom E: the switch runs inside an IIFE so the per-block font
  // wrapper (withBlockFont) applies uniformly to every MVP case below.
  // `undefined` = no case matched → delegate to PublicBlockRendererExtended,
  // which applies its own withBlockFont (no double wrapping).
  // WEB-2921: контекст долей ядра (core-blocks/) — один объект на отрисовку.
  const coreCtx: CoreRenderContext = {
    block,
    stylePackId,
    publicStrings,
    varCtx,
    stripePrices,
  };
  const rendered = ((): React.ReactElement | null | undefined => {
    switch (block.type) {
      case 'title':
        return (
          <h1
            style={{
              // WEB-1108 п.4: на десктопе колонка пейволла шире (960 по
              // умолчанию, public-paywall.css), но чисто текстовым блокам во
              // всю эту ширину читаться неудобно — своя переменная с фолбэком
              // 100%, её задаёт ТОЛЬКО новый десктопный медиа-запрос пейволла;
              // без него (квиз, мобильный вид) правило инертно, вид не меняется.
              maxWidth: 'var(--paywall-text-max-w, 100%)',
              marginLeft: 'auto',
              marginRight: 'auto',
              // WEB-447 wave1 slice2: responsive display-scale (was a flat
              // 28px) — clamps between a mobile floor and a desktop ceiling so
              // long titles never overflow small viewports. See
              // state/research/web447-funnel-teardowns/DESIGN-LANGUAGE.md §2.2.
              // WEB-665: размер S/M/L (умолч. M — текущий responsive-scale).
              // Атом 5: размер/вес/трекинг по умолчанию берутся из стиль-пака,
              // фолбэки равны прежним значениям (WEB-447 wave1 slice2), поэтому
              // без пака вид не меняется. Ручной размер S/L сильнее пака.
              // Запрос владельца продукта 10.08: точный px (block.fontSize)
              // сильнее пресета size — absent оставляет прежний clamp/var.
              fontSize: block.fontSize
                ? `${block.fontSize}px`
                : block.size === 'sm'
                  ? 'clamp(22px, calc(var(--vp-w, 100vw) * 0.04), 32px)'
                  : block.size === 'lg'
                    ? 'clamp(34px, calc(var(--vp-w, 100vw) * 0.06), 52px)'
                    : 'var(--h1-size, clamp(28px, calc(var(--vp-w, 100vw) * 0.05), 40px))',
              // Повтор эталона Slimkit (typography.md п.2): интерлиньяж из
              // пака (поле h1LineHeight его типографики); без него — прежние 1.15.
              lineHeight: titleLineHeight(stylePackId),
              letterSpacing: 'var(--h1-tracking, -0.015em)',
              fontWeight:
                'var(--h1-weight, 700)' as React.CSSProperties['fontWeight'],
              marginBottom: 16,
              textAlign: (block.align ??
                'var(--heading-align, center)') as React.CSSProperties['textAlign'],
              color: primaryColor,
              // WEB-447 A3: heading font var chain — falls back to the body font
              // var, then to inherit, so pages without an AI-selected font are
              // unaffected (heading keeps inheriting the body font as before).
              fontFamily:
                'var(--font-family-heading, var(--font-family-primary, inherit))',
              // Слово владельца 20.10.2026 (задание stylepack/text): свой
              // вид текста — накладкой ПОСЛЕ решения пака; пусто — {}.
              ...textLookOverride(block),
            }}
          >
            {renderTitleSideImage(block, textWithIcon(block.content))}
          </h1>
        );

      case 'sub_title':
        return (
          <p
            style={{
              // WEB-1108 п.4 (см. 'title' выше — тот же приём/переменная).
              maxWidth: 'var(--paywall-text-max-w, 100%)',
              marginLeft: 'auto',
              marginRight: 'auto',
              // Запрос владельца продукта 10.08: точный px (block.fontSize)
              // сильнее пресета size — absent оставляет прежний маппинг.
              fontSize:
                block.fontSize ??
                (block.size === 'sm' ? 14 : block.size === 'lg' ? 19 : 16),
              // WEB-1702 п.2: блочный цвет сильнее общего цвета воронки.
              color: contentTextColor ?? 'var(--color-text-secondary, #595959)',
              // Повтор эталона Slimkit (typography.md п.2): интерлиньяж и трекинг
              // подзаголовка из пака — см. subTitleTypeStyle.
              ...subTitleTypeStyle(stylePackId),
              marginBottom: 12,
              textAlign: (block.align ??
                'var(--heading-align, center)') as React.CSSProperties['textAlign'],
              // Слово владельца 20.10.2026 (задание stylepack/text): свой
              // вид текста — накладкой ПОСЛЕ решения пака; пусто — {}.
              ...textLookOverride(block),
            }}
          >
            {renderTitleSideImage(block, textWithIcon(block.content))}
          </p>
        );

      case 'heading':
        return (
          <h2
            style={{
              // WEB-1108 п.4 (см. 'title' выше — тот же приём/переменная).
              maxWidth: 'var(--paywall-text-max-w, 100%)',
              marginLeft: 'auto',
              marginRight: 'auto',
              // WEB-447 wave1 slice2: responsive scale (was a flat 22px).
              // Запрос владельца продукта 10.08: точный px (block.fontSize)
              // сильнее пресета size — absent оставляет прежний clamp/var.
              fontSize: block.fontSize
                ? `${block.fontSize}px`
                : block.size === 'sm'
                  ? 'clamp(18px, calc(var(--vp-w, 100vw) * 0.03), 22px)'
                  : block.size === 'lg'
                    ? 'clamp(26px, calc(var(--vp-w, 100vw) * 0.05), 36px)'
                    : 'var(--q-size, clamp(22px, calc(var(--vp-w, 100vw) * 0.04), 28px))',
              lineHeight: 1.2,
              letterSpacing: 'var(--q-tracking, -0.01em)',
              fontWeight:
                'var(--q-weight, 600)' as React.CSSProperties['fontWeight'],
              marginBottom: 12,
              textAlign: block.align ?? 'left',
              color: primaryColor,
              // WEB-447 A3: same heading font var chain as the 'title' block above.
              fontFamily:
                'var(--font-family-heading, var(--font-family-primary, inherit))',
              // Слово владельца 20.10.2026 (задание stylepack/text): свой
              // вид текста — накладкой ПОСЛЕ решения пака; пусто — {}.
              ...textLookOverride(block),
            }}
          >
            {textWithIcon(block.content)}
          </h2>
        );

      case 'paragraph':
        return (
          <p
            style={{
              // WEB-1108 п.4 (см. 'title' выше — тот же приём/переменная).
              maxWidth: 'var(--paywall-text-max-w, 100%)',
              marginLeft: 'auto',
              marginRight: 'auto',
              // Запрос владельца продукта 10.08: точный px (block.fontSize)
              // сильнее пресета size — absent оставляет прежний маппинг.
              fontSize:
                block.fontSize ??
                (block.size === 'sm' ? 14 : block.size === 'lg' ? 19 : 16),
              lineHeight: 1.6,
              marginBottom: 12,
              textAlign: block.align ?? 'left',
              color: primaryColor,
              // Слово владельца 20.10.2026 (задание stylepack/text): свой
              // вид текста — накладкой ПОСЛЕ решения пака; пусто — {}.
              ...textLookOverride(block),
            }}
          >
            {textWithIcon(block.content)}
          </p>
        );

      case 'image':
        // imageUrl is a URL field — NOT interpolated (D7)
        if (!block.imageUrl) return null;
        if (imageLoadError) {
          return (
            <AuthorHintBox
              style={{
                // WEB-1911 (класс WEB-966): подложка и текст заглушки —
                // ОДНА пара переменных ПОВЕРХНОСТИ воронки. Раньше фон брался
                // из темы кабинета, а текст — из `--color-text-secondary`,
                // который у тёмной темы белый: заглушка «картинка не
                // загрузилась» гасла на светлой канве. Цвет текста теперь
                // даёт сам AuthorHintBox (`--color-text-on-surface`).
                background: 'var(--color-bg-surface, #fafafa)',
              }}
            >
              {getPublicQuizStrings(varCtx?.system?.locale).imageLoadError}
            </AuthorHintBox>
          );
        }
        {
          // WEB-1686: кадр картинки (вписывание, видимая часть, высота или
          // пропорции, выравнивание) собирается ОДНИМ помощником. Прежний вид
          // — включая width 100% из WEB-907 и marginBottom, которым владеет
          // WEB-1680, — возвращается оттуда объявление в объявление, когда ни
          // одно поле кадра не задано.
          const hasChip = hasText(block.captionChip);
          const img = (
            <img
              src={block.imageUrl}
              alt={interp(block.imageAlt)}
              onError={() => setImageLoadError(true)}
              decoding="async"
              style={reserveImageSpace(imageFrameStyle(block, hasChip))}
            />
          );

          // Атом 8: подпись-пилюля ПОВЕРХ снимка (референс Aspect Health).
          // Без чипа картинка рендерится как раньше — лишней обёртки не
          // появляется, чтобы не менять вёрстку существующих воронок.
          if (!hasChip) return img;

          const pos = block.captionChipPosition ?? 'top-left';
          const vertical = pos.startsWith('top') ? { top: 12 } : { bottom: 28 };
          const horizontal = pos.endsWith('left')
            ? { left: 12 }
            : { right: 12 };
          // WEB-1684: цвет, кегль и фон подписи — авторские, но со страховкой
          // читаемости над произвольным снимком. Правила и причина, по которой
          // они были зашиты, разобраны в captionChipAppearance.ts; здесь
          // сознательно нет ни одного числа и ни одной переменной, иначе
          // появилась бы вторая правда.
          const chipLook = captionChipAppearance(block);

          return (
            // WEB-1686 п.3: при выравнивании обёртка обжимается по картинке —
            // иначе угол подписи считался бы от обёртки во всю ширину блока, а
            // снимок стоял бы своим размером у края, и подпись повисла бы в
            // пустоте. Без настроек кадра обёртка прежняя.
            <div style={imageWrapperStyle(block)}>
              {img}
              <span
                data-testid="image-caption-chip"
                style={{
                  position: 'absolute',
                  ...vertical,
                  ...horizontal,
                  padding: '6px 14px',
                  borderRadius: 'var(--border-radius-button, 999px)',
                  background: chipLook.background,
                  color: chipLook.color,
                  fontSize: chipLook.fontSize,
                  fontWeight: 600,
                  maxWidth: 'calc(100% - 24px)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {interp(block.captionChip)}
              </span>
            </div>
          );
        }

      case 'html':
        // 🔴 XSS-INVARIANT: values inside block.html are HTML-escaped via interpHtml.
        // Template HTML (author-written <strong>, <p>, etc.) passes through unchanged.
        return block.html ? (
          <div
            style={{ marginBottom: 16 }}
            dangerouslySetInnerHTML={{ __html: interpHtml(block.html) }}
          />
        ) : null;

      case 'single_choice': {
        // WEB-1060: режим выбора решает, куда садится подложка ВЫБРАННОЙ опции
        // (--option-selected-bg ниже) — а значит и то, каким токеном обязан
        // краситься её текст (см. optionSelectedColor.ts).
        const singleChoiceSelectedMode = stylePackId
          ? getStylePackById(stylePackId)?.states.selected
          : undefined;
        // Кольцо выбора ring-пака держит форму варианта — своя тень автора
        // не должна его вытеснить (optionRowShadow).
        const optionRing =
          (stylePackId
            ? getStylePackById(stylePackId)?.shape.selectionMode
            : undefined) === 'ring';
        return (
          <div style={{ marginBottom: 16 }}>
            {hasText(block.content) && (
              <p
                style={{
                  fontWeight: 600,
                  marginBottom: 12,
                  color: primaryColor,
                  // Дозаказ владельца продукта: px-размер вопроса — options
                  // (варианты ответа) остаются данными ответа, их не трогаем.
                  ...(block.fontSize ? { fontSize: block.fontSize } : {}),
                }}
              >
                {richText(block.content)}
              </p>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--option-gap, 8px)',
                // Слово владельца 20.10.2026: свой промежуток между
                // вариантами — накладкой ПОСЛЕ переменной пака; пусто — {}.
                ...optionGapOverride(block),
                // Задание stylepack/option-grid: 2–3 столбца сеткой.
                ...optionListGridOverride(block),
              }}
            >
              {block.options?.map((opt) => (
                <label
                  key={opt.id}
                  className="funnel-option"
                  data-testid={`option-${opt.id}`}
                  data-selected={
                    isOptionSelected(block.id, opt.id) ? 'true' : 'false'
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    // Текст варианта по центру из пака (outline-pills, WEB-3718);
                    // у прочих паков и без пака — {}, разметка прежняя.
                    ...optionAlignOverride(stylePackId),
                    gap: 10,
                    // Задание stylepack/option-content-gap: свой зазор
                    // значка и текста — ПОСЛЕ зашитых 10; пусто — {}.
                    ...optionContentGapOverride(block),
                    // Плотность и форма опции — из стиль-пака; фолбэки равны
                    // прежним значениям, поэтому воронка без пака выглядит как
                    // раньше (атом 5).
                    minHeight: optionMinHeightOf(
                      block,
                      'var(--option-h, auto)',
                    ),
                    padding:
                      'var(--option-pad-y, 10px) var(--option-pad-x, 14px)',
                    // Выбранное состояние из стиль-пака: заливка и рамка идут
                    // через свои токены, чтобы пак мог задать «рамка+заливка»,
                    // «залитый» или кольцо, не трогая невыбранные опции.
                    background: isOptionSelected(block.id, opt.id)
                      ? 'var(--option-selected-bg, var(--color-bg-surface, transparent))'
                      : undefined,
                    // WEB-1674: своя заливка варианта — накладкой ПОСЛЕ строки
                    // выше, поэтому она перебивает решение стиль-пака (до неё
                    // подложку решал ТОЛЬКО пак, а у невыбранного её не было
                    // вовсе). Пустые поля спредят {} — вид прежний.
                    ...optionBackgroundOverride(
                      block,
                      isOptionSelected(block.id, opt.id),
                    ),
                    border: isOptionSelected(block.id, opt.id)
                      ? 'var(--option-border-width, 2px) solid var(--option-selected-border, var(--color-primary, #1677ff))'
                      : 'var(--option-border-width, 2px) solid var(--option-border-color, var(--color-border-primary, #f0f0f0))',
                    borderRadius:
                      'var(--radius-option, var(--border-radius-md, 8px))',
                    // В ring-режиме пака рамка обнулена, форму держит кольцо;
                    // своя тень — первым слоем, своя рамка гасит кольцо у
                    // невыбранного (optionRowShadow).
                    ...optionRowShadow(
                      block,
                      isOptionSelected(block.id, opt.id),
                      optionRing,
                    ),
                    // Слово владельца 20.10.2026: своё скругление — накладкой
                    // ПОСЛЕ решения пака. Пусто — {}.
                    ...optionRadiusOverride(block),
                    ...optionRowIndicatorOverride(block),
                    ...optionIndicatorRowOverride(block),
                    // Задание stylepack/text: свои отступ и рамка плашки —
                    // накладками ПОСЛЕ решения пака; пусто — {}.
                    ...optionPaddingOverride(
                      block,
                      'var(--option-pad-y, 10px)',
                      'var(--option-pad-x, 14px)',
                    ),
                    // Своя рамка ОТМЕЧЕННОЙ плашки (задание
                    // stylepack/selected-look) сильнее выбора пака; разница
                    // толщин снимается с отступа — раскладка не прыгает.
                    ...optionBorderOverride(
                      block,
                      isOptionSelected(block.id, opt.id),
                      OPTION_PACK_BORDER,
                      OPTION_PACK_PAD,
                    ),
                    fontSize: 'var(--option-size, inherit)',
                    // WEB-1673: свой кегль вариантов — накладкой ПОСЛЕ строки
                    // выше, иначе его всегда перебивал --option-size стиль-пака
                    // (в этом и был корень: inherit не срабатывал никогда).
                    // Пустое поле спредит {} — размер решает пак, как раньше.
                    ...optionFontSizeOverride(block),
                    fontWeight:
                      'var(--option-weight, inherit)' as React.CSSProperties['fontWeight'],
                    // Задание stylepack/text: своя жирность подписи — ПОСЛЕ пака.
                    ...optionFontWeightOverride(block),
                    // Задание stylepack/option-grid: ячейка сетки сжимается,
                    // длинная подпись переносится внутри. Пусто — {}.
                    ...optionCellOverride(block),
                    transition:
                      'background-color var(--transition-ms, 0ms) ease, border-color var(--transition-ms, 0ms) ease',
                    cursor: 'pointer',
                    // WEB-1060: текст ВЫБРАННОЙ опции обязан контрастировать со
                    // своей подложкой (см. background выше), а не с фоном
                    // страницы — раньше здесь был безусловный primaryColor,
                    // из-за чего фото-воронки со светлым текстом получали
                    // «белым по белому» на выбранной опции.
                    color: resolveSelectedOptionColor(
                      singleChoiceSelectedMode,
                      primaryColor,
                      isOptionSelected(block.id, opt.id),
                    ),
                    // WEB-1672: явный цвет ВАРИАНТОВ от автора — накладкой
                    // ПОСЛЕ строки выше, поэтому он сильнее авто-подстройки.
                    // Пустое поле спредит {} — старая воронка байт-в-байт та же.
                    ...optionTextColorOverride(
                      block,
                      isOptionSelected(block.id, opt.id),
                    ),
                  }}
                >
                  <input
                    type="radio"
                    name={block.id}
                    value={opt.id}
                    checked={blockValue === opt.id}
                    onChange={() => setValue(block.id, opt.id)}
                    // WEB-1651-3: сама отметка красится выбором автора, иначе
                    // палитрой воронки — системно-синего браузера больше нет.
                    style={{
                      width: 18,
                      height: 18,
                      accentColor: resolveOptionAccent(block.optionAccentColor),
                      // Задание stylepack/option-indicator-align: 'top' — у верха
                      // строки; пусто — {}.
                      ...optionIndicatorAlignOverride(block),
                      // «Без отметки» (слово владельца 20.10.2026): поле
                      // остаётся для клика и клавиатуры, кружка/галочки нет.
                      ...(optionIndicatorHidden(block)
                        ? OPTION_INDICATOR_HIDDEN_STYLE
                        : {}),
                      // Задание stylepack/option-indicator: справа — в конец
                      // строки; свой вид «box» — поле спрятано, как выше.
                      ...optionIndicatorInputOverride(block),
                    }}
                  />
                  {/* Задание stylepack/option-grid: значки варианта;
                      stylepack/option-description: описание под подписью. */}
                  <OptionRowContent
                    fields={block}
                    icon={opt.icon}
                    description={optionDescriptionNode(opt)}
                    centered={packOptionCentered(stylePackId)}
                  >
                    {optionDisplay(opt)}
                  </OptionRowContent>
                  <OptionIndicatorBox
                    fields={block}
                    kind="single"
                    checked={blockValue === opt.id}
                  />
                </label>
              ))}
            </div>
            {/* Задание stylepack/scale-labels: подписи концов шкалы одной
                строкой под рядом вариантов; нет подписей — ничего. */}
            <OptionScaleLabels fields={block} />
          </div>
        );
      }

      case 'multiple_choice': {
        // WEB-1060: см. single_choice выше — тот же режим пака решает и
        // подложку, и обязанный ей цвет текста выбранной опции.
        const multipleChoiceSelectedMode = stylePackId
          ? getStylePackById(stylePackId)?.states.selected
          : undefined;
        // Кольцо выбора ring-пака держит форму варианта — своя тень автора
        // не должна его вытеснить (optionRowShadow).
        const optionRing =
          (stylePackId
            ? getStylePackById(stylePackId)?.shape.selectionMode
            : undefined) === 'ring';
        return (
          <div style={{ marginBottom: 16 }}>
            {hasText(block.content) && (
              <p
                style={{
                  fontWeight: 600,
                  marginBottom: 12,
                  color: primaryColor,
                  // Дозаказ владельца продукта: px-размер вопроса — options
                  // (варианты ответа) остаются данными ответа, их не трогаем.
                  ...(block.fontSize ? { fontSize: block.fontSize } : {}),
                }}
              >
                {richText(block.content)}
              </p>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--option-gap, 8px)',
                // Слово владельца 20.10.2026: свой промежуток между
                // вариантами — накладкой ПОСЛЕ переменной пака; пусто — {}.
                ...optionGapOverride(block),
                // Задание stylepack/option-grid: 2–3 столбца сеткой.
                ...optionListGridOverride(block),
              }}
            >
              {block.options?.map((opt) => (
                <label
                  key={opt.id}
                  className="funnel-option"
                  data-testid={`option-${opt.id}`}
                  data-selected={
                    isOptionSelected(block.id, opt.id) ? 'true' : 'false'
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    // Текст варианта по центру из пака (outline-pills, WEB-3718);
                    // у прочих паков и без пака — {}, разметка прежняя.
                    ...optionAlignOverride(stylePackId),
                    gap: 10,
                    // Задание stylepack/option-content-gap: свой зазор
                    // значка и текста — ПОСЛЕ зашитых 10; пусто — {}.
                    ...optionContentGapOverride(block),
                    // Плотность и форма опции — из стиль-пака; фолбэки равны
                    // прежним значениям, поэтому воронка без пака выглядит как
                    // раньше (атом 5).
                    minHeight: optionMinHeightOf(
                      block,
                      'var(--option-h, auto)',
                    ),
                    padding:
                      'var(--option-pad-y, 10px) var(--option-pad-x, 14px)',
                    // Выбранное состояние из стиль-пака: заливка и рамка идут
                    // через свои токены, чтобы пак мог задать «рамка+заливка»,
                    // «залитый» или кольцо, не трогая невыбранные опции.
                    background: isOptionSelected(block.id, opt.id)
                      ? 'var(--option-selected-bg, var(--color-bg-surface, transparent))'
                      : undefined,
                    // WEB-1674: своя заливка варианта — накладкой ПОСЛЕ строки
                    // выше, поэтому она перебивает решение стиль-пака (до неё
                    // подложку решал ТОЛЬКО пак, а у невыбранного её не было
                    // вовсе). Пустые поля спредят {} — вид прежний.
                    ...optionBackgroundOverride(
                      block,
                      isOptionSelected(block.id, opt.id),
                    ),
                    border: isOptionSelected(block.id, opt.id)
                      ? 'var(--option-border-width, 2px) solid var(--option-selected-border, var(--color-primary, #1677ff))'
                      : 'var(--option-border-width, 2px) solid var(--option-border-color, var(--color-border-primary, #f0f0f0))',
                    borderRadius:
                      'var(--radius-option, var(--border-radius-md, 8px))',
                    // В ring-режиме пака рамка обнулена, форму держит кольцо;
                    // своя тень — первым слоем, своя рамка гасит кольцо у
                    // невыбранного (optionRowShadow).
                    ...optionRowShadow(
                      block,
                      isOptionSelected(block.id, opt.id),
                      optionRing,
                    ),
                    // Слово владельца 20.10.2026: своё скругление — накладкой
                    // ПОСЛЕ решения пака. Пусто — {}.
                    ...optionRadiusOverride(block),
                    ...optionRowIndicatorOverride(block),
                    ...optionIndicatorRowOverride(block),
                    // Задание stylepack/text: свои отступ и рамка плашки —
                    // накладками ПОСЛЕ решения пака; пусто — {}.
                    ...optionPaddingOverride(
                      block,
                      'var(--option-pad-y, 10px)',
                      'var(--option-pad-x, 14px)',
                    ),
                    // Своя рамка ОТМЕЧЕННОЙ плашки (задание
                    // stylepack/selected-look) сильнее выбора пака; разница
                    // толщин снимается с отступа — раскладка не прыгает.
                    ...optionBorderOverride(
                      block,
                      isOptionSelected(block.id, opt.id),
                      OPTION_PACK_BORDER,
                      OPTION_PACK_PAD,
                    ),
                    fontSize: 'var(--option-size, inherit)',
                    // WEB-1673: свой кегль вариантов — накладкой ПОСЛЕ строки
                    // выше, иначе его всегда перебивал --option-size стиль-пака
                    // (в этом и был корень: inherit не срабатывал никогда).
                    // Пустое поле спредит {} — размер решает пак, как раньше.
                    ...optionFontSizeOverride(block),
                    fontWeight:
                      'var(--option-weight, inherit)' as React.CSSProperties['fontWeight'],
                    // Задание stylepack/text: своя жирность подписи — ПОСЛЕ пака.
                    ...optionFontWeightOverride(block),
                    // Задание stylepack/option-grid: ячейка сетки сжимается,
                    // длинная подпись переносится внутри. Пусто — {}.
                    ...optionCellOverride(block),
                    transition:
                      'background-color var(--transition-ms, 0ms) ease, border-color var(--transition-ms, 0ms) ease',
                    cursor: 'pointer',
                    // WEB-1060: см. single_choice — текст выбранной опции обязан
                    // читаться на СВОЕЙ подложке, а не на фоне страницы.
                    color: resolveSelectedOptionColor(
                      multipleChoiceSelectedMode,
                      primaryColor,
                      isOptionSelected(block.id, opt.id),
                    ),
                    // WEB-1672: см. single_choice — накладка цвета вариантов
                    // ПОСЛЕ строки выше; пустое поле спредит {}.
                    ...optionTextColorOverride(
                      block,
                      isOptionSelected(block.id, opt.id),
                    ),
                  }}
                >
                  <input
                    type="checkbox"
                    name={block.id}
                    value={opt.id}
                    checked={(blockValue ?? '').split(',').includes(opt.id)}
                    onChange={(e) => {
                      const current = (blockValue ?? '')
                        .split(',')
                        .filter(Boolean);
                      const next = e.target.checked
                        ? Array.from(new Set([...current, opt.id]))
                        : current.filter((id) => id !== opt.id);
                      setValue(block.id, next.join(','));
                    }}
                    // WEB-1651-3: см. single_choice выше — та же отметка.
                    style={{
                      width: 18,
                      height: 18,
                      accentColor: resolveOptionAccent(block.optionAccentColor),
                      // Задание stylepack/option-indicator-align: 'top' — у верха
                      // строки; пусто — {}.
                      ...optionIndicatorAlignOverride(block),
                      // «Без отметки» (слово владельца 20.10.2026): поле
                      // остаётся для клика и клавиатуры, кружка/галочки нет.
                      ...(optionIndicatorHidden(block)
                        ? OPTION_INDICATOR_HIDDEN_STYLE
                        : {}),
                      // Задание stylepack/option-indicator: справа — в конец
                      // строки; свой вид «box» — поле спрятано, как выше.
                      ...optionIndicatorInputOverride(block),
                    }}
                  />
                  {/* Задание stylepack/option-grid: значки варианта;
                      stylepack/option-description: описание под подписью. */}
                  <OptionRowContent
                    fields={block}
                    icon={opt.icon}
                    description={optionDescriptionNode(opt)}
                    centered={packOptionCentered(stylePackId)}
                  >
                    {optionDisplay(opt)}
                  </OptionRowContent>
                  <OptionIndicatorBox
                    fields={block}
                    kind="multiple"
                    checked={(blockValue ?? '').split(',').includes(opt.id)}
                  />
                </label>
              ))}
            </div>
            {/* Задание stylepack/scale-labels: подписи концов шкалы одной
                строкой под рядом вариантов; нет подписей — ничего. */}
            <OptionScaleLabels fields={block} />
          </div>
        );
      }

      case 'email':
      case 'text_field':
        return (
          <div style={{ marginBottom: 16 }}>
            {hasText(block.content) && (
              <label
                style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: 8,
                  color: 'var(--color-text-primary, inherit)',
                  // WEB-1694: свой цвет и кегль ПОДПИСИ — накладкой ПОСЛЕ
                  // строки выше. Пустые поля спредят {}: вид прежний до байта.
                  ...inputLabelStyleOverride(block),
                }}
              >
                {richText(block.content)}
              </label>
            )}
            <input
              type={block.type === 'email' ? 'email' : 'text'}
              name={block.id}
              value={blockValue ?? ''}
              onChange={(e) => setValue(block.id, e.target.value)}
              placeholder={
                // WEB-1907 (добор 2): пустое поле — старая воронка, блок ИИ
                // или стёртый автором текст; английский литерал показывал
                // «Enter your email» покупателю русской воронки. Слово — из
                // словаря плеера по языку воронки, как у соседних фолбэков.
                // WEB-2973: текст автора — через подстановку.
                block.placeholder != null
                  ? interp(block.placeholder)
                  : block.type === 'email'
                    ? publicStrings.emailInputPlaceholder
                    : publicStrings.textInputPlaceholder
              }
              data-block-type={block.type}
              data-testid={`public-input-${block.id}`}
              className={inputLookClassName(block)}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '2px solid var(--color-border-primary, #d9d9d9)',
                borderRadius: 'var(--border-radius-md, 8px)',
                fontSize: 16,
                boxSizing: 'border-box',
                background: 'var(--color-bg-surface, #fff)',
                color:
                  'var(--color-text-on-surface, var(--color-text-primary, inherit))',
                // WEB-1694: свой кегль и цвет ТЕКСТА ВНУТРИ поля — накладками
                // ПОСЛЕ зашитых строк выше (16px и переменные поверхности).
                // Пустые поля спредят {}: старая воронка не меняется.
                ...inputTextFontSizeOverride(block),
                ...inputTextColorOverride(block),
                // Слово владельца 21.10.2026: вид САМОГО поля — накладкой
                // ПОСЛЕ зашитых строк выше; пусто — {}, решает стиль-пак.
                ...inputBoxStyleOverride(block),
              }}
            />
          </div>
        );

      case 'checkbox':
        return (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 16,
              cursor: 'pointer',
              color: primaryColor,
            }}
          >
            <input
              type="checkbox"
              name={block.id}
              checked={blockValue === 'true'}
              onChange={(e) =>
                setValue(block.id, e.target.checked ? 'true' : 'false')
              }
              // WEB-1651-3: одиночная галочка согласия — та же отметка.
              style={{
                width: 18,
                height: 18,
                accentColor: resolveOptionAccent(block.optionAccentColor),
              }}
            />
            <span
              style={block.fontSize ? { fontSize: block.fontSize } : undefined}
            >
              {/* WEB-1907 (добор 2): фолбэк подписи — язык воронки. */}
              {richText(block.content, publicStrings.agreeLabel)}
            </span>
          </label>
        );

      case 'sensitive_consent':
        return (
          <SensitiveConsentBlock
            block={block}
            primaryColor={primaryColor}
            collectLabel={richText(
              block.content,
              publicStrings.blockDefaults.sensitiveConsentCollect,
            )}
            shareLabel={richText(
              (block as { sensitiveShareContent?: string })
                .sensitiveShareContent,
              publicStrings.blockDefaults.sensitiveConsentShare,
            )}
            note={publicStrings.sensitiveConsentNote}
          />
        );

      case 'button_next': {
        if (skipOnlyActive(block)) {
          return <SkipOnlyLink block={block} onSkip={onSkip ?? onAdvance} />;
        }
        const nextStyle: React.CSSProperties = {
          // WEB-447 wave1 slice2: reads the theme's buttonStyle
          // (solid/outlined/pill/gradient) — previously always a solid
          // rectangle regardless of theme (mert token).
          ...primaryButtonStyle(buttonStyle, textColor),
          width: '100%',
          ...advanceButtonStyle,
          // Запрос владельца продукта 10.08 + WEB-1651-2: «Фон кнопки»
          // и «Заливка элемента»/«Цвет текста внутри элемента» —
          // перекрывают акцент темы ТОЛЬКО когда заданы
          // (buttonSurfaceStyle возвращает {} на пустых полях — старые
          // воронки не меняются). Приоритеты — в blockBackgroundStyle.
          ...buttonSurfaceStyle(block),
          // Запрос владельца продукта 10.08: точный px размера текста
          // кнопки — сильнее дефолтного fontSize из primaryButtonStyle.
          ...(block.fontSize ? { fontSize: block.fontSize } : {}),
          // Слово владельца 20.10.2026 (задание stylepack/text): своя
          // высота и жирность подписи — последними; пусто — {}.
          ...buttonLookOverride(block, false),
        };
        return (
          <div
            style={{
              textAlign: 'center',
              marginBottom: 16,
              ...advanceWrapStyle,
            }}
          >
            {/* Задание stylepack/next-back-pair: при backBeside и
                обработчике «назад» — ряд «назад» + «далее»; иначе кнопка
                «далее» как была, без обёртки. */}
            <NextBackPair
              block={block}
              onBack={onBack}
              backLabel={publicStrings.backLabel}
              nextStyle={nextStyle}
            >
              <button
                // WEB-1696: обработчик перехода и приглушение приходят от
                // визарда — сам рендер экранами не управляет. Без них кнопка
                // остаётся декоративной ровно как раньше.
                onClick={onAdvance}
                // Ревизия пачки 8 (данные лида): нажатие кнопки продвижения не
                // отбирает фокус у поля ввода. Иначе `mousedown` уводил фокус,
                // всплывавший `focusout` включал авто-переход экрана (WEB-1643),
                // экран сменялся ДО `click` — и клик приземлялся уже на новом
                // экране (переход через один, сквозной тап в вариант ответа).
                // Разбор механизма — keepFieldFocusOnPress в QuizScreenWizard.
                // Ставится только там, где кнопка реально продвигает экран: без
                // `onAdvance` (канва конструктора) поведение прежнее. Пейволл в
                // этот список больше не входит — с WEB-1977 его визард отдаёт
                // `button_next` сюда вместе с `onAdvance` (ревизия пачки 28, R4).
                onMouseDown={onAdvance ? (e) => e.preventDefault() : undefined}
                data-testid={advanceTestId}
                className={advanceDimmed ? 'funnel-next-unfilled' : undefined}
                style={nextStyle}
              >
                {/* WEB-1696: подпись-фолбэк задаёт визард — на последнем экране
                    квиза это «Завершить», а не «Продолжить». Не передана —
                    прежнее английское 'Continue'.
                    WEB-1779 (решение владельца 03.09, вариант А): подпись берётся
                    через buttonLabelField — `buttonText`, а при пустом
                    `buttonText` второе поле `content`, куда её положил каталог
                    шаблонов (144 подписи 25 нишевых квизов). Воронки с
                    заполненным `buttonText` не меняются. */}
                {richText(
                  buttonLabelField(block),
                  advanceFallbackLabel ?? 'Continue',
                )}
              </button>
            </NextBackPair>
            <SkipLinkButton block={block} onSkip={onSkip ?? onAdvance} />
          </div>
        );
      }

      case 'button_link': {
        // D7: block.url is a URL field — NOT interpolated.
        //
        // WEB-1906 (вариант б владельца 05.09): отдельная ветка ДО safeHref.
        // Маркер `w2a://store` — это НЕ интерполяция и не адрес: генерация
        // ставит его вместо выдуманной ссылки, а настоящий адрес магазина
        // выбирает рантайм по платформе посетителя из настроек проекта
        // (WEB-828). Адресов нет — кнопки нет: fail-closed, выдуманных
        // адресов не бывает ни при каком раскладе (п.3 приёмки WEB-828).
        // Автору в превью конструктора вместо тишины говорим словами, почему
        // кнопки не будет.
        let href = block.url;
        if (isStoreAutoUrl(block.url)) {
          const resolved = resolveStoreAutoUrl(
            { ios: storeLinks.ios, android: storeLinks.android },
            typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          );
          if (!resolved) {
            return isEditorPreview ? (
              <AuthorHintBox
                testId={`store-auto-url-missing-${block.id}`}
                compact
              >
                {pickStrings(authorHintsStrings).storeAutoUrlMissing}
              </AuthorHintBox>
            ) : null;
          }
          href = resolved;
        }
        return (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <a
              href={safeHref(href)}
              // WEB-2129 п.2: вкладку выбирает автор. Поля нет — прежнее
              // `_blank` (класс WEB-1119: собранные воронки не меняются);
              // `false` — уход в той же вкладке, чтобы не рвать путь
              // покупателя перед чужим пейволлом. Умолчание НОВОГО блока —
              // «та же вкладка», его ставит заготовка (blockDefaults.ts).
              target={block.openInNewTab === false ? '_self' : '_blank'}
              // `noreferrer` оставлен и для той же вкладки: адрес нашей
              // страницы не должен уезжать чужому серверу ни в одном из двух
              // случаев (общее правило Referrer-Policy публичного входа).
              rel="noopener noreferrer"
              // WEB-2124: уход по кнопке-ссылке — основной способ отправить
              // человека на чужой пейволл у клиентов «только квизы», и он не
              // фиксировался вовсе. Засчитываем завершение и досылаем очередь
              // ДО того, как браузер уведёт. Досыл нужен в ОБЕИХ вкладках, а в
              // той же — особенно: страница уезжает насовсем, и не досланная
              // очередь пропала бы вместе с ней. С WEB-3296 опубликованная
              // кнопка уходит через серверную точку ухода (денежная запись
              // перехода, WEB-3295); аналитического шага сервер по ней
              // по-прежнему не пишет (WEB-2164 п.6).
              onClick={
                isEditorPreview
                  ? (e) => e.preventDefault()
                  : externalExit
                    ? (e) => {
                        // Порядок строк критичен и тот же, что у остальных
                        // веток ухода: сперва завершение и досыл очереди
                        // (WEB-2124), только потом браузер уезжает.
                        externalExit.markLinkExit();
                        const cleaned = safeHref(href);
                        // WEB-3296: опубликованная кнопка уводит через
                        // серверную точку ухода (адрес сервер берёт из
                        // опубликованной версии, метки подставляет сам).
                        // Мимо точки: маркер магазина (адреса в блоке нет,
                        // сервер его не разрешит) и адрес, не прошедший
                        // дверь очистки (пусто, невидимые знаки, чужая
                        // схема) — сервер ответил бы 404, а человек до
                        // WEB-3296 оставался на странице (возврат 1 приёмки).
                        const exitHref =
                          cleaned === '#' || isStoreAutoUrl(block.url)
                            ? null
                            : (externalExit.linkExitHref?.(block.id) ?? null);
                        // Плеер точку не собрал — прежний путь WEB-2262:
                        // метки подставляются в момент нажатия, адрес без
                        // меток остаётся родным переходом по ссылке.
                        const macroHref =
                          exitHref === null
                            ? linkMacroHref(
                                cleaned,
                                externalExit.outboundMacroValues?.(),
                              )
                            : null;
                        if (exitHref === null && macroHref === null) return;
                        // WEB-1911: уезжает только то, что прошло дверь
                        // очистки; запасное значение — адрес автора без
                        // меток, а не пустой «#».
                        const target = safeHref(exitHref ?? macroHref, cleaned);
                        e.preventDefault();
                        if (block.openInNewTab === false) {
                          window.location.href = target;
                        } else {
                          // `noopener,noreferrer` повторяет `rel` ссылки: адрес
                          // нашей страницы не уезжает чужому серверу и в этой
                          // ветке тоже.
                          window.open(target, '_blank', 'noopener,noreferrer');
                        }
                      }
                    : undefined
              }
              style={{
                display: 'inline-block',
                padding: '12px 32px',
                background: 'transparent',
                color: textColor ?? 'var(--color-primary, #1677ff)',
                // WEB-1697: рамка блока — из справочника умолчаний
                // (blockBorder.ts), а не числом здесь. Пусто = прежние
                // `2px solid` акцентом и скругление кнопки.
                ...resolveBlockBorder(block),
                // Запрос владельца продукта 10.08: точный px размера текста
                // кнопки — сильнее прежнего фиксированного 16px.
                fontSize: block.fontSize ?? 16,
                fontWeight: 600,
                textDecoration: 'none',
                width: '100%',
                boxSizing: 'border-box',
                // Запрос владельца продукта 10.08 + WEB-1651-2: «Фон кнопки»
                // — button_link по умолчанию outlined/прозрачная; заданный фон
                // заливает её (обводку под solid/translucent/blur снимает сам
                // хелпер), а innerTextColor перекрывает цвет надписи выше.
                ...buttonSurfaceStyle(block),
                // Задание stylepack/text: своя высота (с центровкой подписи)
                // и жирность — последними; пусто — {}.
                ...buttonLookOverride(block, true),
              }}
            >
              {/* WEB-1779: та же дыра каталога, второй тип кнопки — у 25
                  нишевых квизов подпись кнопки со ссылкой тоже лежит в
                  `content`, и клиент видел английское 'Learn more' на русской
                  странице. Правило одно на оба типа (buttonLabelField).
                  WEB-1907 (добор 2): сам фолбэк был последней половиной той же
                  дыры — литерал не зависел от языка воронки. Ключ словаря
                  learnMoreLabel уже существовал и читается умолчанием кнопки
                  в конструкторе, теперь и рендером. */}
              {richText(buttonLabelField(block), publicStrings.learnMoreLabel)}
            </a>
          </div>
        );
      }

      case 'redirect_url':
        return (
          <RedirectBlock
            blockId={block.id}
            url={block.url}
            delaySeconds={block.redirectDelaySeconds}
            showUrl={block.redirectShowUrl === true}
            strings={publicStrings}
          />
        );

      case 'progress_bar': {
        const value = Math.max(0, Math.min(100, block.progressValue ?? 50));
        return (
          // WEB-785: testid — визард снимает блок-дубль индикатора при видимой
          // шапке, и это должно быть проверяемо тестом/QA-селектором.
          <div
            data-testid={`progress-bar-${block.id}`}
            style={{ marginBottom: 16 }}
          >
            <div
              style={{
                height: 8,
                background: 'var(--color-border-primary, #f0f0f0)',
                borderRadius: 'var(--border-radius-sm, 4px)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${value}%`,
                  background: 'var(--color-primary, #1677ff)',
                  transition: 'width 0.3s',
                }}
              />
            </div>
          </div>
        );
      }

      case 'spinner': {
        // WEB-1698 (репорт владельца 24.08). У подписи ТРИ состояния, а не два,
        // и раньше два последних были склеены выражением
        // `interp(spinnerLabel) || словарь`:
        //   · поля нет вовсе (undefined) — автор подпись не задавал: словарное
        //     слово на языке воронки (WEB-785), ровно как до правки. Это гард
        //     на живые воронки: там, где подпись не трогали, вид не меняется;
        //   · поле есть и пусто — автор УБРАЛ подпись сознательно: не рисуется
        //     ни текст, ни его контейнер с отступом (раньше контейнер стоял
        //     всегда, и даже с пустой строкой под кружком оставался зазор);
        //   · поле есть и непусто — печатается авторский текст.
        const rawSpinnerLabel = block.spinnerLabel;
        const spinnerLabelText =
          rawSpinnerLabel === undefined
            ? publicStrings.loadingLabel
            : interp(rawSpinnerLabel);
        const showSpinnerLabel = spinnerLabelText.trim() !== '';
        return (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div
              data-testid="spinner-ring"
              style={{
                display: 'inline-block',
                width: 32,
                height: 32,
                // WEB-1698: два цвета кольца — большая часть (по которой
                // крутится маленькая) и сама вращающаяся часть. Пусто =
                // прежние переменные темы, то есть сегодняшний вид.
                // Порядок обязателен: краткая запись `border` сбрасывает
                // border-top-color, поэтому вращающаяся часть идёт ПОСЛЕ неё.
                // Размер (32) и толщина (4) сознательно оставлены числом —
                // владелец про них не просил, а их место в общем справочнике
                // умолчаний блока (WEB-1678/WEB-1680), а не здесь.
                border: `4px solid ${
                  block.spinnerTrackColor ||
                  'var(--color-border-primary, #f0f0f0)'
                }`,
                borderTopColor:
                  block.spinnerAccentColor || 'var(--color-primary, #1677ff)',
                borderRadius: '50%',
                animation: 'pbr-spin 0.8s linear infinite',
              }}
            />
            <style>{`@keyframes pbr-spin { to { transform: rotate(360deg); } }`}</style>
            {showSpinnerLabel && (
              <div
                data-testid="spinner-label"
                style={{
                  // WEB-1692 юнит 7: подпись — ЕДИНСТВЕННЫЙ текст блока,
                  // поэтому кегль общий (`fontSize`), а не своя пара роли;
                  // цвет ей уже даёт общее «Цвет текста внутри элемента»
                  // (WEB-1702). Пусто = прежние 14px.
                  fontSize: block.fontSize ?? 14,
                  color: 'var(--color-text-secondary, #8c8c8c)',
                  marginTop: 8,
                }}
              >
                {/* WEB-785: подпись спиннера — на языке воронки. */}
                {spinnerLabelText}
              </div>
            )}
          </div>
        );
      }

      case 'terms_checkbox': {
        // termsUrl and privacyUrl are URL fields — NOT interpolated (D7)
        // WEB-901 (5-й заход, QA 10.08): предыдущие версии ВЫРЕЗАЛИ упомянутые
        // автором названия документов из текста и ПЕРЕСТАВЛЯЛИ их отдельным
        // блоком в конец — рабочим это выглядело только пока названия у автора
        // и так стояли в конце фразы; в середине давало рваный обрубок с
        // висящей пунктуацией. Теперь авторский текст не режется и не
        // переставляется вовсе: уже упомянутые названия превращаются в ссылки
        // НА МЕСТЕ упоминания (linkifyLegalMentions), а блок ниже дописывается
        // только для документа, которого автор не упомянул — иначе был бы
        // дубль. AI-путь получает то же самое через общий рендер (и уже
        // подчищен пост-ген-гардом WEB-896 — операция идемпотентна).
        const {
          content: termsContent,
          termsMentioned,
          privacyMentioned,
        } = linkifyLegalMentions(
          asRichField(block.content),
          {
            termsUrl: block.termsUrl,
            privacyUrl: block.privacyUrl,
          },
          // WEB-2384 п.6: язык воронки — иначе названия документов узнавались
          // только в английских и русских воронках, а на остальных языках
          // упоминание автора не становилось ссылкой и рендер дописывал
          // названия второй раз (дубль в строке согласия).
          varCtx?.system?.locale,
        );
        const termsPlain =
          termsContent == null ? '' : richFieldToPlainText(termsContent);
        // Дописываем блок после текста, только если хотя бы один из двух
        // документов не был упомянут автором (иначе дописывать нечего).
        const appendTerms = !termsMentioned;
        const appendPrivacy = !privacyMentioned;
        const appendBlock = appendTerms || appendPrivacy;
        // WEB-936: между авторским текстом и первой ссылкой нужен пробел —
        // раньше склеивалось «продолжитьTerms». Дефолт «I accept the » свой
        // пробел уже несёт, поэтому пустой текст ничего не добавляет. Если
        // дописывать нечего (оба документа уже упомянуты в тексте автора),
        // пробел не нужен вовсе.
        const termsNeedsSpace =
          appendBlock && termsPlain !== '' && !/\s$/.test(termsPlain);
        return (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 16,
              cursor: 'pointer',
              // WEB-1692 юнит 7: названия документов — ССЫЛКИ ВНУТРИ ТОЙ ЖЕ
              // ФРАЗЫ, а не второй по смыслу текст, поэтому кегль на всю
              // строку один и двусмысленности «какое поле красит» нет.
              // Пусто = прежние 14px.
              fontSize: block.fontSize ?? 14,
              color: primaryColor,
            }}
          >
            {/* WEB-895 (возврат QA 2026-08-01): галочка была НЕуправляемой — её
              отметка не попадала в FormValues. На пейволле дыру затыкал
              делегированный onChange визарда (WEB-890 п.7), а на экране КВИЗА
              такой подпорки нет: отмеченный чекбокс запирал пользователя
              навсегда. Пишем значение у источника — работает в обоих визардах. */}
            <input
              type="checkbox"
              name={block.id}
              required={block.required ?? true}
              checked={blockValue === 'true'}
              onChange={(e) =>
                setValue(block.id, e.target.checked ? 'true' : 'false')
              }
              // WEB-1651-3: галочка принятия условий — та же отметка (размер
              // у неё исторически 16, его не трогаем).
              style={{
                width: 16,
                height: 16,
                accentColor: resolveOptionAccent(block.optionAccentColor),
              }}
            />
            <span>
              {/* WEB-785: вводная фраза и названия документов — на языке
                воронки; из них и складывалась английская «Terms and Privacy
                Policy» поверх русской страницы (это НЕ строка от модели). */}
              <RichTextContent
                value={termsContent}
                varCtx={varCtx}
                fallback={publicStrings.termsAcceptPrefix}
              />
              {termsNeedsSpace ? ' ' : null}
              {/* WEB-901 (5-й заход): дописываем только документ, которого
                автор ещё не упомянул в тексте (termsMentioned/privacyMentioned
                выше) — упомянутый уже стал ссылкой на месте, повторная
                дописка дала бы дубль. */}
              {/* WEB-890 п.6: без адреса — НЕ рендерим мёртвую <a>; текст остаётся. */}
              {appendTerms &&
                (block.termsUrl ? (
                  <a
                    href={safeHref(block.termsUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: LINK_ON_SURFACE_COLOR }}
                  >
                    {interp(block.termsLabel) || publicStrings.termsDocLabel}
                  </a>
                ) : (
                  <span>
                    {interp(block.termsLabel) || publicStrings.termsDocLabel}
                  </span>
                ))}
              {/* QA WEB-702: соединитель редактируемый (absent = "and") — нужен,
                только когда дописываются ОБА документа. */}
              {appendTerms && appendPrivacy
                ? ` ${(block.separatorLabel != null ? interp(block.separatorLabel) : publicStrings.termsSeparator).trim()} `
                : null}
              {appendPrivacy &&
                (block.privacyUrl ? (
                  <a
                    href={safeHref(block.privacyUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: LINK_ON_SURFACE_COLOR }}
                  >
                    {interp(block.privacyLabel) ||
                      publicStrings.privacyDocLabel}
                  </a>
                ) : (
                  <span>
                    {interp(block.privacyLabel) ||
                      publicStrings.privacyDocLabel}
                  </span>
                ))}
            </span>
          </label>
        );
      }

      case 'stripe_checkout_form':
        return (
          // WEB-1860: атрибута `data-stripe-price` здесь НЕТ намеренно.
          // Раньше он печатал СОБСТВЕННУЮ цену блока, а не ту, которая
          // спишется, — и врал глазу проверяющего через инспектор. Денег это
          // не касалось: на платёжном пути ветка недостижима
          // (PaywallScreenWizard подменяет этот тип на BuyNowButton ВЫШЕ), а у
          // кнопки нет обработчика нажатия — она статична. Отрисоваться она
          // всё же может (экран КВИЗА через QuizScreenWizard, превью
          // редактора), поэтому атрибут убран, а не пересчитан: платёжного
          // контекста (живого выбора тарифа) тут нет, гнать его через
          // resolvePriceId значило бы завести второй источник правды о цене.
          // Живой атрибут списываемой цены — у BuyNowButton и у ветки
          // `stripe_wallets_express` (обе через resolvePriceId).
          <button
            data-checkout-mode="hosted"
            style={{
              width: '100%',
              padding: '14px 24px',
              background: 'var(--color-primary, #635bff)',
              color: 'var(--color-on-primary, #fff)',
              border: 'none',
              borderRadius: 'var(--border-radius-button, 8px)',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: 16,
              // Запрос владельца продукта 10.08 + WEB-1651-2: «Фон кнопки» и
              // цвет текста из панели — перекрывают фиксированный цвет ТОЛЬКО
              // когда заданы.
              ...buttonSurfaceStyle(block),
            }}
          >
            {/* WEB-1907 (добор 2): не payLabel («Купить» / «Buy Now») — здесь
                СПОСОБ оплаты, отдельный ключ payWithCardLabel. Механику
                кнопки не трогаем: она статична (см. WEB-1860 выше). */}
            {publicStrings.payWithCardLabel}
          </button>
        );

      case 'stripe_wallets_express':
        // WEB-704 (вариант A): кнопки кошельков запускают тот же hosted Stripe
        // Checkout, что и «Buy» (Stripe сам показывает Apple/Google Pay на своей
        // странице). Хук usePaywallCheckout нельзя звать в switch → компонент.
        return (
          <WalletsExpressButtons
            locale={varCtx?.system?.locale}
            block={block}
            screenBlocks={screenBlocks}
            funnelBlocks={funnelBlocks}
            hiddenBlockIds={hiddenBlockIds}
          />
        );

      case 'payment_banners':
        // WEB-2921, заход 2: тело ветки — доля core-blocks/ (слово в слово).
        return renderPaymentBanners(coreCtx);

      case 'result_screen':
        return (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <h2
              style={{
                // Запрос владельца продукта 10.08: точный px размера — сильнее
                // прежнего фиксированного 24px.
                fontSize: block.fontSize ?? 24,
                fontWeight: 700,
                marginBottom: 8,
                color: primaryColor,
              }}
            >
              {/* WEB-1907 (добор 2): фолбэк — completedTitle словаря. */}
              {richText(block.content, publicStrings.completedTitle)}
            </h2>
          </div>
        );

      case 'price_block':
        // WEB-2921, заход 2: тело ветки — доля core-blocks/ (слово в слово).
        return renderPriceBlock(coreCtx);

      case 'button_to_pay':
        return (
          // WEB-1860: атрибута `data-stripe-price` здесь НЕТ намеренно — та же
          // оговорка, что у ветки `stripe_checkout_form` выше: платёжный путь
          // сюда не доходит (перехват в PaywallScreenWizard), обработчика
          // нажатия нет, а печать собственной цены блока врала проверяющему в
          // инспекторе на экране квиза и в превью редактора.
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <button
              style={{
                // WEB-447 wave1 slice2: primary accent (was hardcoded
                // success-green #52c41a — DESIGN-LANGUAGE §3 item 4), theme
                // buttonStyle-aware (solid/outlined/pill/gradient).
                ...primaryButtonStyle(buttonStyle, textColor),
                width: '100%',
                ...advanceButtonStyle,
                // Запрос владельца продукта 10.08 + WEB-1651-2: «Фон кнопки» и
                // цвет текста из панели — перекрывают акцент темы ТОЛЬКО
                // когда заданы.
                ...buttonSurfaceStyle(block),
                // Запрос владельца продукта 10.08: точный px размера текста
                // кнопки — сильнее дефолтного fontSize из primaryButtonStyle.
                ...(block.fontSize ? { fontSize: block.fontSize } : {}),
              }}
            >
              {/* WEB-785 (возврат QA 01.08): фолбэк был зашит по-английски. */}
              {richText(block.buttonText, publicStrings.payLabel)}
            </button>
          </div>
        );

      case 'timer':
        // WEB-2921, заход 1: тело ветки — функция отрисовки доли
        // core-blocks/TimerRenderer.tsx (перенесено слово в слово).
        return renderTimer(coreCtx);

      default:
        return undefined;
    }
  })();

  if (rendered !== undefined) {
    // WEB-1062: подложка блока — общая обёртка на выходе, единая для ЛЮБОГО
    // типа (см. blockBackgroundStyle.tsx withBlockBackground); withBlockFont
    // снаружи, как и раньше.
    // WEB-1440: поверхность самого элемента — та же схема «одна обёртка на
    // выходе», между шрифтом и подложкой: переменные должны накрывать и
    // содержимое подложки тоже.
    return withBlockFont(
      block.fontId,
      withBlockInnerSurface(
        // WEB-1711: у блока, который сам красит свою поверхность значением
        // поля, заливка обёртке не отдаётся — иначе переменная поддерева
        // покрасила бы вложенную кнопку.
        innerSurfaceFieldsOf(block),
        // WEB-1708: обёртка расстояния — СНАРУЖИ обёртки подложки. Внутри неё
        // авторские поля заперты в коробке с внутренним отступом: наружу они
        // не выходят и зазора до соседних блоков не создают, а лишь добавляют
        // пустоту внутри карточки. Прежнее зашитое число корня гасит
        // global.css — правило перечисляет и путь сквозь подложку.
        withBlockSpacing(
          block,
          // WEB-1695: положение блока по горизонтали — общая обёртка ВНУТРИ
          // подложки: плашка остаётся во всю ширину, а содержимое едет к
          // нужному краю. Кому и каким способом — blockAlign.tsx; блок без
          // заданного значения обёртки не получает вовсе (старая воронка не
          // сдвигается).
          withBlockBackground(
            block.background,
            withBlockAlign(block, rendered),
            block,
          ),
          { followsExplicitSpacing },
        ),
      ),
    );
  }
  // Виды первого экрана, которые рисует ядерный общий файл, — мимо двери к
  // ленивым кускам: куска у них нет вовсе.
  if (CORE_COMMON_BLOCK_TYPES.includes(block.type)) {
    return (
      <PublicBlockRendererCommon
        block={block}
        textColor={textColor}
        varCtx={varCtx}
        stripePrices={stripePrices}
        buttonStyle={buttonStyle}
        stepPosition={stepPosition}
        stylePackId={stylePackId}
        screenBlocks={screenBlocks}
        funnelBlocks={funnelBlocks}
        hiddenBlockIds={hiddenBlockIds}
        followsExplicitSpacing={followsExplicitSpacing}
      />
    );
  }

  return (
    <PublicBlockRendererGate
      block={block}
      textColor={textColor}
      varCtx={varCtx}
      stripePrices={stripePrices}
      buttonStyle={buttonStyle}
      stepPosition={stepPosition}
      stylePackId={stylePackId}
      screenBlocks={screenBlocks}
      // WEB-1302 (хвост): без funnelBlocks блок «Условия оплаты» во вложенном
      // рендерере не видел «Выбор тарифа» с предыдущего экрана — под кнопкой,
      // которая спишет выбранный тариф, печаталась статичная цена из настроек.
      // hiddenBlockIds — та же лестница, что у кнопки: скрытый правилами
      // селектор не диктует цену и юр-тексту (второй заслон resolvePriceId).
      funnelBlocks={funnelBlocks}
      hiddenBlockIds={hiddenBlockIds}
      // WEB-1709: признак соседства обязан доехать и до типов, которые рисует
      // вложенный рендерер, — иначе «одна дверь из двух».
      followsExplicitSpacing={followsExplicitSpacing}
    />
  );
}

/**
 * WEB-1993 п.3 — блок экрана перерисовывается, только когда изменилось то,
 * что он показывает.
 *
 * Почему нужен свой сравниватель, а не голый `React.memo`. Плеер передаёт
 * блокам `varCtx` — контекст подстановок ({{ответ}}, {{utm_source}}…). Он
 * пересобирается на КАЖДЫЙ ответ покупателя (новый объект), поэтому обычное
 * поверхностное сравнение считало бы разными все блоки экрана после любого
 * тапа, и мемоизация не дала бы ничего.
 *
 * Пропуск разрешён ТОЛЬКО по белому списку (`VARCTX_INERT_BLOCK_TYPES`) —
 * это блоки-содержимое, которые из `varCtx` берут ровно две вещи: подстановку
 * в свой текст и язык. Значит, блок пропускаем, когда:
 *   - его тип в белом списке, И
 *   - в самом блоке нет ни одной подстановки `{{…}}` (проверяем весь блок
 *     целиком, вместе с вложенным содержимым), И
 *   - язык и страна в контексте те же.
 * Всё остальное (варианты, поля, кнопки, цены, таймеры, сводки ответов,
 * любой НОВЫЙ тип блока) сравнивается как раньше — по ссылке на `varCtx`, то
 * есть перерисовывается. Белый список безопасен по построению: незнакомое
 * ему поведение — «перерисовать», а не «замереть с устаревшим видом».
 */
const VARCTX_INERT_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'title',
  'sub_title',
  'heading',
  'paragraph',
  'image',
]);

const interpolationFlagCache = new WeakMap<object, boolean>();

function blockHasInterpolation(block: EditorBlock): boolean {
  const cached = interpolationFlagCache.get(block as unknown as object);
  if (cached !== undefined) return cached;
  let flag = true;
  try {
    flag = JSON.stringify(block).includes('{{');
  } catch {
    // Не сериализуется — считаем, что подстановки есть (перерисуем).
    flag = true;
  }
  interpolationFlagCache.set(block as unknown as object, flag);
  return flag;
}

export function arePublicBlockPropsEqual(
  prev: PublicBlockRendererProps,
  next: PublicBlockRendererProps,
): boolean {
  const keys = new Set<string>([...Object.keys(prev), ...Object.keys(next)]);
  for (const key of keys) {
    if (key === 'varCtx') continue;
    const a = (prev as unknown as Record<string, unknown>)[key];
    const b = (next as unknown as Record<string, unknown>)[key];
    if (!Object.is(a, b)) return false;
  }
  if (Object.is(prev.varCtx, next.varCtx)) return true;
  if (!VARCTX_INERT_BLOCK_TYPES.has(next.block.type)) return false;
  if (blockHasInterpolation(next.block)) return false;
  return (
    prev.varCtx?.system?.locale === next.varCtx?.system?.locale &&
    prev.varCtx?.system?.geoCountry === next.varCtx?.system?.geoCountry
  );
}

export const PublicBlockRenderer = memo(
  PublicBlockRendererInner,
  arePublicBlockPropsEqual,
);
